import { useEffect } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Alert, Card, Spinner } from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type { AdminSession, Operator } from "../admin-client";
import { useSignedInOperator } from "../admin-hooks";

/**
 * can answers "does this operator hold that permission", and is used for ONE
 * thing: deciding whether to render a control.
 *
 * Hiding a button is a courtesy to the operator, never a control. The Admin
 * API re-checks the permission on every route it guards (internal/admin's
 * Guard middleware, deny by default) and writes the refusal to the audit
 * trail, so an operator who reaches a route they may not use is refused there
 * — this only spares them from finding out by clicking.
 */
export function can(
  operator: Operator | undefined,
  permission: string,
): boolean {
  return operator?.permissions.includes(permission) ?? false;
}

/**
 * isPlatformStaff answers "does this operator see across tenants".
 *
 * The absence of `tenant_id` IS the privilege, inverted from the NULL on the
 * catalogue tables where it means "a default every tenant inherits" (see
 * admin.admin_users in 0047). An operator we have not loaded yet is NOT
 * platform staff: the safe answer while `ready` is false is the narrower one,
 * so a hard refresh cannot flash a platform-only section at a tenant operator.
 */
export function isPlatformStaff(operator: Operator | undefined): boolean {
  return operator !== undefined && operator.tenant_id === undefined;
}

/**
 * tenantLabel is what the shell prints for "which tenant am I in".
 *
 * A back office serving more than one company has to state that on every
 * screen for the same reason it states which operator is signed in: the
 * console looks identical whichever tenant it is pointed at, and a suspension
 * applied to the wrong company is applied irreversibly. The slug is preferred
 * over the display name because it is the handle that appears in the seed,
 * the URLs and the operator's own address.
 */
export function tenantLabel(operator: Operator | undefined): string {
  if (!operator) {
    return "";
  }
  if (isPlatformStaff(operator)) {
    return "Platform";
  }
  return (
    operator.tenant_slug ||
    operator.tenant_name ||
    operator.tenant_id ||
    "Tenant"
  );
}

/** The path a sign-in should come back to, kept relative so it can only ever
 *  return the operator to this origin. */
export function currentReturnPath(): string {
  return window.location.pathname + window.location.search;
}

/**
 * safeReturnPath accepts only a same-origin, non-protocol-relative path.
 *
 * `next` arrives in a URL, which means it arrives from whoever wrote the
 * link. Anything that could leave this origin is dropped rather than
 * sanitised — a back office that can be made to bounce an operator to another
 * site is an open redirect wearing an admin cookie.
 */
export function safeReturnPath(next: string | undefined): string | undefined {
  if (!next || !next.startsWith("/") || next.startsWith("//")) {
    return undefined;
  }
  return next;
}

/**
 * RequireOperator renders its children only for a signed-in operator, and
 * sends everyone else to sign in with a way back.
 *
 * The `ready` check is the part that matters: `signedIn` is false during the
 * first session fetch too, so redirecting on `!signedIn` alone bounces a
 * signed-in operator to the sign-in screen on every hard refresh.
 */
export function RequireOperator({
  children,
}: {
  children: (session: AdminSession) => ReactNode;
}) {
  const { ready, signedIn, session } = useSignedInOperator();
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && !signedIn) {
      void navigate({
        to: "/sign-in",
        search: { next: currentReturnPath() },
        replace: true,
      });
    }
  }, [ready, signedIn, navigate]);

  if (!ready) {
    return <Spinner label="Checking your session" />;
  }
  if (!session) {
    return (
      <Card title="Sign in to continue">
        <p>
          <Link to="/sign-in" search={{ next: currentReturnPath() }}>
            Sign in
          </Link>{" "}
          with an operator account to see this page.
        </p>
      </Card>
    );
  }
  return <>{children(session)}</>;
}

/** The human-facing text for anything thrown by the client. */
export function errorMessage(
  err: unknown,
  fallback = "Something went wrong. Try again.",
): string {
  if (isApiError(err)) {
    return err.message;
  }
  if (err instanceof Error && err.message !== "") {
    return err.message;
  }
  return fallback;
}

export function ErrorAlert({
  error,
  title,
}: {
  error: unknown;
  title?: string;
}) {
  if (!error) {
    return null;
  }
  return (
    <Alert tone="danger" title={title ?? "That did not work"}>
      {errorMessage(error)}
    </Alert>
  );
}
